<template>
  <section id="trackingRequest" class="page-content">
      <header id="section-header">
          <p class="title has-text-grey">Tracking request form</p>
          <p class="subtitle has-text-grey">Create a new tracking request.</p>
      </header>
      <main id="section-body">
        <div class="field">
          <label class="label">Carrier *</label>
          <div class="control">
            <div class="select">
              <select placeholder="Choose Carrier" name="carrier" id="carrier" v-model="selectedCarrier" @click="trackingRequest.carrier_id = selectedCarrier.id" required=true>
                <option v-for="carrier in carriers" :key="carrier.id" :value="carrier">{{ carrier.official_name }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="field" v-if="projects.length">
          <label class="label">Project</label>
          <div class="control">
            <div class="select">
              <select placeholder="Choose Project" name="project" id="project" v-model="selectedProject" @click="trackingRequest.project_id = selectedProject.id">
                <option selected value> ---------- </option>
                <option v-for="project in projects" :key="project.id" :value="project">{{ project.id }} - {{ project.name }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="field">
          <label class="label">Code Type *</label>
          <div class="control">
            <div class="select">
              <select placeholder="Choose Code Type" name="code_type" id="code_type" v-model="trackingRequest.code_type" required=true>
                <option v-if="!trackingRequest.carrier_id || (selectedCarrier && selectedCarrier.request_type_support[codeType.id])"
                 v-for="(codeType, index) in codeTypes" :key="index" :value="codeType.id">{{ codeType.name }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="field" :class="{'half-page': requiredContainer}">
          <label class="label">Code Value *</label>
          <div class="control">
            <input class="input" type="text" placeholder="Code Value" name="code_value" id="code_value" v-model="trackingRequest.code_value" required=true>
          </div>
        </div>
        <div class="field half-page" v-if="requiredContainer">
          <label class="label">Container
            <span class="fa-stack fa-sm" title="This shipping company requires to indicate at least one container number with the Booking or BL.">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-question fa-stack-1x fa-inverse"></i>
            </span>
          </label>
          <div class="control">
            <input class="input" type="text" placeholder="Container" name="container" id="container" v-model="trackingRequest.booking_bl_required_container">
          </div>
        </div>
        <div class="field">
          <label class="label">Client Code</label>
          <div class="control">
            <input class="input" type="text" placeholder="Client Code" name="client_code" id="client_code" v-model="trackingRequest.client_code">
          </div>
        </div>
        <div>
          <div class="field half-page">
            <label class="label">Contractual ETA at POD</label>
            <datepicker
                  :inputClass="'input'"
                  :format="'yyyy-MM-dd'"
                  :placeholder="'YYYY-MM-DD'"
                  :monday-first=true
                  :typeable=true
                  :clear-button=true
                  :clear-button-icon="'fa fa-times'"
                  v-model="trackingRequest.contractual_eta_pod"
                  />
          </div>
          <div class="field half-page">
            <label class="label">Contractual ETA at DLV</label>
            <datepicker
                  :inputClass="'input'"
                  :format="'yyyy-MM-dd'"
                  :placeholder="'YYYY-MM-DD'"
                  :monday-first=true
                  :typeable=true
                  :clear-button=true
                  :clear-button-icon="'fa fa-times'"
                  v-model="trackingRequest.contractual_eta_dlv"
                  />
          </div>
        </div>
        <div>
          <div class="field half-page">
            <label class="label">Storage Free Days</label>
            <input class="input" type="number" placeholder="Storage Free Days" name="storage_free_days" id="storage_free_days" v-model="trackingRequest.storage_free_days">
          </div>
          <div class="field half-page">
            <label class="label">Demurrage Free Days</label>
            <input class="input" type="number" placeholder="Demurrage Free Days" name="demurrage_free_days" id="demurrage_free_days" v-model="trackingRequest.demurrage_free_days">
          </div>
          <div class="field half-page">
            <label class="label">Detention Free Days</label>
            <input class="input" type="number" placeholder="Detention Free Days" name="detention_free_days" id="detention_free_days" v-model="trackingRequest.detention_free_days">
          </div>
        </div>
        <div class="field">
          <label class="label">Container Filter
            <span class="fa-stack fa-sm" title="This field allows to limit the tracking only to the container numbers indicated from the Booking or BL.">
                <i class="fa fa-circle fa-stack-2x"></i>
                <i class="fa fa-question fa-stack-1x fa-inverse"></i>
            </span>
          </label>
          <div class="control">
            <tag-input placeholder="Container Filter" :checkFormat="true" @updated="trackingRequest.container_filter=$event"></tag-input>
          </div>
        </div>

        <a class="button is-primary" @click="save()">Save</a>
        <router-link :to="{ name: 'tracking-request-list'}" class="button">Back</router-link>
        <span class="buttons-right">
          <input type="file" ref="file" @change="uploadBulk"/>
          <a class="button is-primary" @click="$refs.file.click()">Upload Bulk</a>
          <a class="button is-primary" href="../../static/docs/TrackingRequest_template.xlsx" download>Download Bulk Template</a>
        </span>
      </main>
  </section>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import DataTable from '@/components/DataTable'
import TagInput from '@/components/TagInput'
import Datepicker from 'vuejs-datepicker'

export default {
  name: 'trackingRequestAdd',
  components: {DataTable, TagInput, Datepicker},
  created () {
    this.$emit('loading')
    const promises = [
      this.getCarriers().then(response => { this.carriers = response }),
      this.getProjects().then(
        response => {
          this.projects = response
        }
      ),
      this.getCodeTypes().then(response => { this.codeTypes = response })
    ]
    Promise.all(promises).then(data => { this.$emit('loaded') })
  },
  data () {
    return {
      trackingRequest: {},
      carriers: {},
      projects: {},
      selectedCarrier: undefined,
      selectedProject: undefined,
      codeTypes: [],
      bulk: {}
    }
  },
  computed: {
    ...mapGetters({hasPermission: 'hasPermission'}),
    requiredContainer: function () {
      var searchFor = 'submitting of container number'
      return ((this.selectedCarrier && this.selectedCarrier.ctt_quality.note) && (this.selectedCarrier.ctt_quality.note.indexOf(searchFor) >= 0) &&
      (this.trackingRequest.code_type === 'b_id' || this.trackingRequest.code_type === 'm_bl'))
    }
  },
  methods: {
    ...mapActions({
      getProjects: 'project/list',
      getCarriers: 'carrier/list',
      getCodeTypes: 'codeType/arrayList',
      getTrackingRequest: 'trackingRequest/retrieve',
      saveTrackingRequest: 'trackingRequest/save',
      uploadBulkTrackingRequest: 'trackingRequest/uploadBulk'
    }),
    save () {
      if (!this.hasPermission('canWrite')) {
        this.$router.push('dashboard')
        return
      }

      if (this.trackingRequest.storage_free_days === '') {
        delete this.trackingRequest['storage_free_days']
      }

      if (this.trackingRequest.demurrage_free_days === '') {
        delete this.trackingRequest['demurrage_free_days']
      }

      if (this.trackingRequest.detention_free_days === '') {
        delete this.trackingRequest['detention_free_days']
      }

      if (this.requiredContainer && !this.trackingRequest.booking_bl_required_container) {
        this.$notify({group: 'global', type: 'error', duration: 4000, title: 'ERROR', text: 'Required input: Container'})
      } else {
        if (this.selectedCarrier && this.selectedCarrier.id) {
          this.trackingRequest.carrier_id = this.selectedCarrier.id
        }
        this.saveTrackingRequest(this.trackingRequest).then(response => {
          this.$router.push({name: 'tracking-request-list'})
        })
      }
    },
    uploadBulk (e) {
      if (!this.hasPermission('canWrite')) {
        this.$router.push('dashboard')
        return
      }
      var files = e.target.files || e.dataTransfer.files
      if (!files.length) return
      this.uploadBulkTrackingRequest(files[0]).then(response => {
        this.$router.push({name: 'tracking-request-list'})
      })
      .catch(() => {
        const input = this.$refs.file
        input.type = 'text'
        input.type = 'file'
      })
    }
  }
}
</script>
<style lang="scss" scoped>
  .fa-sm{
    font-size: .8em;
  }
  .half-page{
    width: 49.7%;
    display: inline-block;
    min-width: 250px;
  }
  .buttons-right{
    float: right;
    input[type="file"] {
      display: none;
    }
    a {
      margin:.1em 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/PageTrackingRequestAdd.vue